"use client";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { formatGiftAmount } from "@/app/libs/gifts/catalog";
export default function GiftCardsMenuItem({
  signedIn,
  onNavigate,
}: {
  signedIn: boolean;
  onNavigate: () => void;
}) {
  const path = usePathname();
  const [balance, setBalance] = useState<number | null>(null);
  useEffect(() => {
    setBalance(null);
    if (!signedIn) return;
    let stopped = false;
    const controller = new AbortController();
    async function refresh() {
      try {
        const r = await fetch("/api/gifts/wallet", {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!r.ok) return;
        const data = await r.json();
        if (!stopped)
          setBalance(
            data.redemptionEnabled
              ? data.gifts
                  .filter((g: { status: string }) => g.status === "ACTIVE")
                  .reduce(
                    (sum: number, g: { balanceCents: number }) =>
                      sum + g.balanceCents,
                    0,
                  )
              : 0,
          );
      } catch {}
    }
    void refresh();
    function visible() {
      if (document.visibilityState === "visible") void refresh();
    }
    document.addEventListener("visibilitychange", visible);
    return () => {
      stopped = true;
      controller.abort();
      document.removeEventListener("visibilitychange", visible);
    };
  }, [signedIn, path]);
  return (
    <Link
      href={signedIn ? "/gifts/wallet" : "/gifts"}
      onClick={onNavigate}
      className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-cyan-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-700"
    >
      <span>My gift cards</span>
      <span
        className="shrink-0 rounded-full bg-sky-100 px-2.5 py-1 text-xs font-semibold text-sky-800"
        aria-label={
          signedIn && balance !== null
            ? `Available gift balance: ${formatGiftAmount(balance)} USD`
            : undefined
        }
      >
        {signedIn
          ? balance === null
            ? "…"
            : `${formatGiftAmount(balance)} USD`
          : "Give a gift"}
      </span>
    </Link>
  );
}
